import { useMemo } from 'react';
import i18next from 'i18next';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '@/components/ui/empty-state';
import { Clock } from 'lucide-react';
import { useAuditLog, type AuditRecord } from '@/routes/case-detail/audit-tab/use-audit';
import { formatDateTime } from '@/routes/disruptions/labels';

// Per-PNR activity — reads the case AUDIT log (who did what, when) and filters to
// THIS sub-case via `targetUrn` (the audited actions — select / ticket / allocate /
// cancel — carry the sub-case as their target), with a correlation/details fallback.
// The audit log is the right per-PNR source (the case timeline is case-level and
// never references the sub-case, which is why the old timeline-based card was always
// empty). Rendered inside the right-column "Activity" tab.

/** Does this audit record concern the given sub-case? */
function concernsSubCase(r: AuditRecord, subCaseUrn: string): boolean {
  if (!subCaseUrn) return false;
  return (
    r.targetUrn === subCaseUrn ||
    r.targetUrn?.includes(subCaseUrn) === true ||
    r.correlationUrn?.includes(subCaseUrn) === true ||
    r.detailsMasked?.includes(subCaseUrn) === true
  );
}

/** Translation KEY per audit action code — this table holds keys, never language
 *  (the copy lives in `i18n/locales/<lng>/pnr.json` under `activity.action.*`).
 *  APPEND-ONLY vocabulary — never repoint an existing code at another key. */
const ACTION_KEY: Record<string, string> = {
  REBOOKING_SELECT: 'rebookingSelect',
  REBOOKING_OVERRIDE: 'rebookingOverride',
  REBOOKING_CANCEL: 'rebookingCancel',
  REBOOKING_REJECT: 'rebookingReject',
  REBOOKING_GENERATE: 'rebookingGenerate',
  TICKET_ISSUE: 'ticketIssue',
  HOTEL_ALLOCATE: 'hotelAllocate',
  HOTEL_BOOK: 'hotelBook',
  RESERVATION_CANCEL: 'reservationCancel',
  VOUCHER_ISSUE: 'voucherIssue',
  PNR_EXCLUDE: 'pnrExclude',
  PII_REVEAL: 'piiReveal',
  // Care desk (feat/care-desk K6) — append-only vocabulary.
  CARE_TOPUP: 'careTopup',
  CARE_OUTCOME: 'careOutcome',
  CARD_CANCEL: 'cardCancel',
  // transport-desk (T4, NEX-464) — ground-transport verbs.
  TRANSPORT_SEAT_MOVE: 'transportSeatMove',
  TRANSPORT_RUN_ADD: 'transportRunAdd',
  TRANSPORT_OWN_MEANS: 'transportOwnMeans',
  TRANSPORT_CAR_DISPATCH: 'transportCarDispatch',
  CHECKIN_MARKED: 'checkinMarked',
  // NEX-614: audit gap fixes — new domain stream actions
  CASE_CREATED: 'caseCreated',
  REBOOKING_SELECTED: 'rebookingSelected',
  NOTIFICATION_DISPATCHED: 'notificationDispatched',
  CONTACT_UPDATED: 'contactUpdated',
  TRANSPORT_OUTCOME: 'transportOutcome',
  PASSENGER_CHECK_IN: 'passengerCheckIn',
  OFFER_PROPOSED: 'offerProposed',
  OFFER_ACCEPTED: 'offerAccepted',
  OFFER_DECLINED: 'offerDeclined',
  STATE_TRANSITION: 'stateTransition',
};

/** Friendly verb for an audit action code (best-effort; falls back to the code).
 *  Reads the live i18next instance rather than a hook so the vocabulary stays a
 *  plain function (it is exported for the vocabulary tests); the panel that
 *  renders it subscribes via `useTranslation`, so a language switch re-renders. */
export function actionLabel(action: string): string {
  const key = ACTION_KEY[action];
  return key
    ? i18next.t(`activity.action.${key}`, { ns: 'pnr' })
    : action.replace(/_/g, ' ').toLowerCase();
}

export function ActivityPanel({ caseUrn, subCaseUrn }: { caseUrn: string; subCaseUrn: string }) {
  const { t } = useTranslation('pnr');
  const audit = useAuditLog(caseUrn);
  const entries = useMemo(() => {
    const all = audit.data ?? [];
    return all
      .filter((r) => concernsSubCase(r, subCaseUrn))
      .slice()
      .sort((a, b) => new Date(b.ts).getTime() - new Date(a.ts).getTime());
  }, [audit.data, subCaseUrn]);

  if (audit.isLoading) {
    return (
      <p className="px-1 py-6 text-sm text-muted-foreground" data-testid="drawer-activity-loading">
        {t('activity.loading')}
      </p>
    );
  }

  if (entries.length === 0) {
    return (
      <EmptyState
        className="border-0"
        icon={<Clock className="h-6 w-6" aria-hidden />}
        title={t('activity.emptyTitle')}
        description={t('activity.emptyBody')}
      />
    );
  }

  return (
    <ol className="space-y-3 px-1" data-testid="drawer-activity">
      {entries.map((r) => (
        <li key={r.auditUrn} className="relative border-l-2 border-border pl-3">
          <span className="absolute -left-[5px] top-1.5 h-2 w-2 rounded-full bg-primary" aria-hidden />
          <div className="flex flex-wrap items-baseline justify-between gap-x-2">
            <span className="text-sm font-medium">{actionLabel(r.action)}</span>
            <span className="text-xs tabular-nums text-muted-foreground">{formatDateTime(r.ts)}</span>
          </div>
          <div className="text-xs text-muted-foreground">
            {t('activity.by', { name: r.operatorDisplayName })}
            {r.detailsMasked ? <span className="ml-1">· {r.detailsMasked}</span> : null}
          </div>
        </li>
      ))}
    </ol>
  );
}
